import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, FileText, Loader2, ShieldAlert } from "lucide-react";
import Swal from "../../utils/apparifySwal";
import {
  getBusinessTrustOverview,
  submitBusinessTrustAppeal,
} from "../services/businessNotificationService";
import { BtnPrimary } from "../../components/erp";

const BUSINESS_STATUS_BADGE = {
  ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60",
  SUSPENDED: "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800/60",
  PENDING: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60",
};

const APPEAL_STATUS_BADGE = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60",
  UNDER_REVIEW: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60",
  REJECTED: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/60",
};

const APPEAL_REASON_MIN_LENGTH = 10;
const APPEAL_REASON_MAX_LENGTH = 2000;

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function getBusinessStatusBadge(status) {
  return BUSINESS_STATUS_BADGE[String(status || "").toUpperCase()] || "bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700";
}

function getAppealStatusBadge(status) {
  return APPEAL_STATUS_BADGE[String(status || "").toUpperCase()] || "bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700";
}

export default function BusinessTrustSafetyTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [appealReason, setAppealReason] = useState("");

  const loadOverview = useCallback(() => {
    setLoading(true);
    return getBusinessTrustOverview()
      .then((response) => {
        setOverview(response.data || null);
      })
      .catch((error) => {
        console.error("Failed to load trust overview", error);
        setOverview(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const appeals = useMemo(
    () => (Array.isArray(overview?.appeals) ? overview.appeals : []),
    [overview?.appeals],
  );
  const activeSuspension = overview?.activeSuspension || null;
  const hasOpenAppeal = useMemo(
    () => appeals.some((appeal) => ["PENDING", "UNDER_REVIEW"].includes(String(appeal.status || "").toUpperCase())),
    [appeals],
  );
  const trimmedAppealLength = appealReason.trim().length;

  const handleSubmitAppeal = async () => {
    const reason = appealReason.trim();
    if (reason.length < APPEAL_REASON_MIN_LENGTH) {
      Swal.fire({ icon: "warning", title: "Add more detail", text: `Please provide at least ${APPEAL_REASON_MIN_LENGTH} characters for the appeal reason.` });
      return;
    }
    if (reason.length > APPEAL_REASON_MAX_LENGTH) {
      Swal.fire({ icon: "warning", title: "Appeal too long", text: "Appeal reasons must be 2000 characters or less." });
      return;
    }

    setSubmitting(true);
    try {
      await submitBusinessTrustAppeal(reason);
      setAppealReason("");
      await loadOverview();
      Swal.fire({ icon: "success", title: "Appeal submitted", text: "Your moderation appeal has been sent for admin review." });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to submit appeal",
        text: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!overview?.business) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
        Trust and safety details are unavailable right now.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
              <ShieldAlert className="h-4 w-4 text-amber-500" />
              Trust & Safety Status
            </div>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
              This page shows the moderation state of your business and any appeal activity. Shopper reports and reporter identities are not exposed here.
            </p>
          </div>
          <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getBusinessStatusBadge(overview.business.status)}`}>
            {overview.business.status}
          </span>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-800/50">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Moderation Flag</div>
            <div className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
              {overview.business.is_moderated ? "Under moderation review" : "No active moderation flag"}
            </div>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-800/50">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Appeal Access</div>
            <div className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
              {overview.canSubmitAppeal ? "Available for active business members" : "Unavailable for this account"}
            </div>
          </div>
        </div>
      </div>

      {activeSuspension ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800/50 dark:bg-amber-950/30">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            Active Suspension
          </div>
          <p className="mt-3 text-sm text-amber-900 dark:text-amber-100">
            {activeSuspension.reason || "No suspension reason was recorded."}
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-amber-800 dark:text-amber-200">
            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5" />
              Until: {activeSuspension.suspend_until ? formatDateTime(activeSuspension.suspend_until) : "Indefinite"}
            </span>
            <span>Started: {formatDateTime(activeSuspension.created_at)}</span>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-800/50 dark:bg-emerald-950/30">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-200">
            <CheckCircle2 className="h-4 w-4" />
            No Active Restrictions
          </div>
          <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-100">
            Your business currently has no active suspension. If moderation changes later, the details will appear here.
          </p>
        </div>
      )}

      {activeSuspension && overview.canSubmitAppeal && !hasOpenAppeal && (
        <div className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
            <FileText className="h-4 w-4 text-neutral-500" />
            Submit Appeal
          </div>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
            Explain why the suspension should be reconsidered. This goes directly to the admin review queue.
          </p>
          <textarea
            value={appealReason}
            onChange={(event) => setAppealReason(event.target.value)}
            maxLength={APPEAL_REASON_MAX_LENGTH}
            rows={5}
            placeholder="Describe what happened, what has been corrected, and why the suspension should be lifted."
            className="mt-4 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:focus:border-neutral-500"
          />
          <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-neutral-500 dark:text-neutral-400">
            <span>
              Appeals require at least {APPEAL_REASON_MIN_LENGTH} detailed characters and support up to {APPEAL_REASON_MAX_LENGTH}.
            </span>
            <span>{appealReason.length}/{APPEAL_REASON_MAX_LENGTH}</span>
          </div>
          <div className="mt-4 flex justify-end">
            <BtnPrimary onClick={handleSubmitAppeal} disabled={submitting || trimmedAppealLength < APPEAL_REASON_MIN_LENGTH}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Submit Appeal
            </BtnPrimary>
          </div>
        </div>
      )}

      {activeSuspension && hasOpenAppeal && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-800 dark:border-blue-800/50 dark:bg-blue-950/30 dark:text-blue-200">
          An appeal for the current suspension is already open. You can track it below while the admin review is in progress.
        </div>
      )}

      <div className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          <FileText className="h-4 w-4 text-neutral-500" />
          Appeal History
        </div>

        {appeals.length === 0 ? (
          <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">No moderation appeals have been submitted for this business.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {appeals.map((appeal) => (
              <div key={appeal.id} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-800/50">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Appeal #{appeal.id}</div>
                    <div className="mt-1 text-sm text-neutral-600 dark:text-neutral-300 whitespace-pre-wrap">{appeal.reason}</div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold ${getAppealStatusBadge(appeal.status)}`}>
                    {appeal.status}
                  </span>
                </div>

                <div className="mt-3 grid gap-2 text-xs text-neutral-500 dark:text-neutral-400 sm:grid-cols-2">
                  <span>Submitted: {formatDateTime(appeal.created_at)}</span>
                  <span>Resolved: {formatDateTime(appeal.resolved_at)}</span>
                  {appeal.suspension_reason && <span>Suspension reason: {appeal.suspension_reason}</span>}
                  {appeal.suspend_until && <span>Suspend until: {formatDateTime(appeal.suspend_until)}</span>}
                </div>

                {appeal.reviewer_notes && (
                  <div className="mt-3 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">Reviewer Notes</div>
                    <div className="mt-1 whitespace-pre-wrap">{appeal.reviewer_notes}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
